import React, { useState, useEffect } from "react";
import DBConfig from "utils/DBConfig";

const TIME_LIMIT = 30; // in minutes
const CACHE_STORE = "cache";

const PageCacheContext = React.createContext();

function openPageCacheDatabase(hasReset = false) {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not available"));
      return;
    }

    const request = window.indexedDB.open(DBConfig.name, DBConfig.version);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        const store = db.createObjectStore(CACHE_STORE, { keyPath: "key" });
        store.createIndex("value", "value", { unique: false });
        store.createIndex("timestamp", "timestamp", { unique: false });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(CACHE_STORE)) {
        resolve(db);
        return;
      }

      db.close();
      if (hasReset) {
        reject(new Error("IndexedDB cache store is missing"));
        return;
      }

      const deleteRequest = window.indexedDB.deleteDatabase(DBConfig.name);
      deleteRequest.onsuccess = () =>
        openPageCacheDatabase(true).then(resolve, reject);
      deleteRequest.onerror = () => reject(deleteRequest.error);
      deleteRequest.onblocked = () =>
        reject(new Error("IndexedDB reset was blocked"));
    };

    request.onerror = () => reject(request.error);
  });
}

function getAllCache() {
  return openPageCacheDatabase().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(CACHE_STORE, "readonly");
        const request = transaction.objectStore(CACHE_STORE).getAll();

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => db.close();
        transaction.onabort = () => {
          db.close();
          reject(transaction.error);
        };
      })
  );
}

function setCacheEntry(key, value) {
  return openPageCacheDatabase().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(CACHE_STORE, "readwrite");
        const request = transaction
          .objectStore(CACHE_STORE)
          .put({ key, value, timestamp: Date.now() });

        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
        transaction.onabort = () => {
          db.close();
          reject(transaction.error);
        };
      })
  );
}

function usePageCache() {
  const context = React.useContext(PageCacheContext);
  if (!context) {
    throw new Error(`usePageCahe must be used within a PageCacheProvider`);
  }

  return context;
}

function PageCacheProvider({ children }) {
  const [cache, setCache] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getAllCache()
      .then((allCache) => {
        const cached = {};
        allCache.forEach((c) => {
          if ((new Date() - new Date(c.timestamp)) / 1000 / 60 < TIME_LIMIT) {
            cached[c.key] = c.value;
          }
        });
        setCache(cached);
      })
      .then(
        () => {},
        (e) => {
          console.error(e);
        }
      )
      .then(() => {
        setLoaded(true);
      });
  }, []);

  const set = React.useCallback(
    (k, v) => {
      setCache((u) => ({ ...u, [k]: v }));
      setCacheEntry(k, v).catch((e) => {
        console.error(e);
      });
    },
    [setCache]
  );

  return (
    <PageCacheContext.Provider value={{ set, cache }}>
      {children(loaded)}
    </PageCacheContext.Provider>
  );
}

export { PageCacheProvider, usePageCache };
